import { z } from 'zod';
import { civilDate, idSchema } from './common.js';
/**
 * Salah as buildings: each prayer stands as one, rated out of five from a
 * daily entry. The definitions live here so the form, the scoring and the
 * server all name the same things the same way.
 */
export const prayerSchema = z.enum(['fajr', 'dhuhr', 'asr', 'maghrib', 'isha']);
export const PRAYERS = [
    { key: 'fajr', name: 'Fajr' },
    { key: 'dhuhr', name: 'Dhuhr' },
    { key: 'asr', name: "'Asr" },
    { key: 'maghrib', name: 'Maghrib' },
    { key: 'isha', name: "'Isha" },
];
export const timelinessSchema = z.enum(['on_time', 'late', 'qada']);
/**
 * Timeliness is not one quality among many: it multiplies the rest. A prayer
 * out of its time with perfect wudu is still a prayer out of its time. There
 * is no "missed": a prayer is caught or made up, and a day left unlogged is
 * neutral.
 */
export const TIMELINESS = [
    { key: 'on_time', label: 'On time', factor: 1 },
    { key: 'late', label: 'Late, in its window', factor: 0.5 },
    { key: 'qada', label: 'Made up later', factor: 0.2 },
];
export const salahMetricSchema = z.enum([
    'cleanliness',
    'attire',
    'wudu_water',
    'sunnah_before',
    'congregation',
    'focus',
    'no_sahw',
    'dhikr_after',
    'sunnah_after',
    'sit_after',
]);
const always = () => true;
/** In the order they happen: getting ready, the prayer, then after it. */
export const SALAH_METRICS = [
    {
        key: 'cleanliness',
        label: 'Clean body & place',
        hint: 'Tahārah',
        input: 'check',
        applies: always,
    },
    {
        key: 'attire',
        label: 'Proper attire',
        hint: 'Dressed for it',
        input: 'check',
        applies: always,
    },
    {
        key: 'wudu_water',
        label: 'Wudu — water spared',
        hint: 'No more than needed',
        input: 'check',
        applies: always,
    },
    {
        key: 'sunnah_before',
        label: 'Sunnah before',
        hint: 'Rawātib',
        input: 'check',
        applies: (p) => p === 'fajr' || p === 'dhuhr',
    },
    {
        key: 'congregation',
        label: 'In congregation',
        hint: "Jamā'ah",
        input: 'check',
        applies: always,
    },
    { key: 'focus', label: 'Focus', hint: "Khushū'", input: 'scale', applies: always },
    {
        key: 'no_sahw',
        label: 'No sajdat al-sahw',
        hint: 'No slip needing it',
        input: 'check',
        applies: always,
    },
    { key: 'dhikr_after', label: 'Dhikr after', hint: 'Adhkār', input: 'check', applies: always },
    {
        key: 'sunnah_after',
        label: 'Sunnah after',
        hint: 'Rawātib',
        input: 'check',
        applies: (p) => p === 'dhuhr' || p === 'maghrib' || p === 'isha',
    },
    {
        key: 'sit_after',
        label: 'Sat a while after',
        hint: 'No rushing off',
        input: 'check',
        applies: always,
    },
];
export const DEFAULT_SALAH_ORDER = [
    'no_sahw',
    'focus',
    'cleanliness',
    'attire',
    'congregation',
    'sunnah_before',
    'sunnah_after',
    'dhikr_after',
    'wudu_water',
    'sit_after',
];
/** Ticks are 0 or 1; focus is 1–5. A metric left out counts as done. */
export const salahMarksSchema = z.record(salahMetricSchema, z.number().min(0).max(5));
export const salahEntryInputSchema = z.object({
    timeliness: timelinessSchema,
    marks: salahMarksSchema.default({}),
    note: z.string().max(2000).nullable().optional(),
});
export const salahEntrySchema = z.object({
    day: civilDate,
    timeliness: timelinessSchema,
    marks: salahMarksSchema,
    note: z.string().nullable(),
    updatedAt: z.string(),
});
export const salahDaySchema = z.object({
    day: civilDate,
    /** That day's prayer out of five; null when nothing was logged. */
    score: z.number().min(0).max(5).nullable(),
    /** The building's rating once that day is counted. */
    rating: z.number().min(0).max(5),
});
export const salahHistorySchema = z.object({
    buildingId: idSchema,
    prayer: prayerSchema,
    rating: z.number().min(0).max(5),
    entries: z.array(salahEntrySchema),
    days: z.array(salahDaySchema),
});
export const salahSettingsSchema = z.object({
    order: z.array(salahMetricSchema).length(SALAH_METRICS.length),
});
//# sourceMappingURL=salah.js.map